// src/components/dashboard/charts/AccuracyChart.js
import React from 'react';

import useMediaQuery from '../../hooks/useMediaQuery';

const formatAccuracy = (value) => (value !== null ? `${Math.round(value)}%` : 'N/A');

// Full-detail view for Desktop
const DesktopAccuracyView = ({ overallAccuracy, sentencesReviewsAccuracy, wordsReviewAccuracy }) => {
  const CircularAccuracy = ({ percentage, label, color, size = 120 }) => {
    const radius = (size - 12) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - ((percentage ?? 0) / 100) * circumference;

    return (
      <div className="flex flex-col items-center">
        <div className="relative" style={{ width: size, height: size }}>
          <svg className="transform -rotate-90" width={size} height={size}>
            <circle cx={size / 2} cy={size / 2} r={radius} strokeWidth="6" fill="none" className="opacity-30 stroke-border" />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={color}
              strokeWidth="6"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-1000 ease-out"
              style={{
                filter: 'drop-shadow(0 0 6px rgba(var(--color-primary-rgb, 249, 115, 22), 0.3))',
              }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl font-bold text-content-primary mb-1">{formatAccuracy(percentage)}</div>
              <div className="text-xs text-content-secondary font-medium">accuracy</div>
            </div>
          </div>
        </div>
        <div className="mt-3 text-sm font-semibold text-content-primary">{label}</div>
      </div>
    );
  };

  const StatsCard = ({ title, value, subtitle, accentColor }) => (
    // Removed opacity from bg-background-secondary/70 -> bg-background-secondary
    <div className="relative p-4 rounded-xl backdrop-blur-sm border-2 border-border overflow-hidden group bg-background-secondary">
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50 dark:from-white/5" />
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute top-0 right-0 w-16 h-16 rounded-full"
          style={{
            backgroundColor: accentColor,
            transform: 'translate(8px, -8px)',
          }}
        />
      </div>
      <div className="relative z-10 text-center">
        <div className="text-2xl font-bold text-content-primary mb-1" style={{ color: accentColor }}>
          {formatAccuracy(value)}
        </div>
        <div className="text-xs text-content-secondary uppercase tracking-wide font-medium">{title}</div>
        {subtitle && <div className="text-xs text-content-secondary mt-1 opacity-70">{subtitle}</div>}
      </div>
    </div>
  );

  return (
    <div className="flex-1 flex flex-col justify-between">
      {/* Removed opacity from bg-background-secondary/70 -> bg-background-secondary */}
      <div className="relative p-6 rounded-2xl border-2 border-border backdrop-blur-sm mb-6 overflow-hidden bg-background-secondary">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50 dark:from-white/5" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary rounded-full -translate-y-16 translate-x-16" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-secondary rounded-full translate-y-12 -translate-x-12" />
        </div>
        <div className="relative z-10 text-center">
          <CircularAccuracy percentage={overallAccuracy} label="Overall Review Accuracy" color="var(--color-primary)" size={140} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <StatsCard title="Sentences" value={sentencesReviewsAccuracy} subtitle="review accuracy" accentColor="var(--color-secondary)" />
        <StatsCard title="Words" value={wordsReviewAccuracy} subtitle="review accuracy" accentColor="var(--color-secondary)" />
      </div>
    </div>
  );
};

// UPDATED: A hierarchical compact view for Mobile
const CompactAccuracyView = ({ overall, sentences, words }) => (
  // Removed opacity from bg-background-secondary/70 -> bg-background-secondary
  <div className="flex flex-col justify-center items-center p-4 rounded-2xl border-2 border-border bg-background-secondary h-full text-center">
    {/* Overall Accuracy */}
    <div className="mb-4">
      <span className="text-4xl font-extrabold text-primary">
        {formatAccuracy(overall)}
      </span>
      <div className="text-sm font-semibold text-content-primary mt-1">Overall Review Accuracy</div>
    </div>
    {/* Divider */}
    <div className="w-full h-px bg-border my-2"></div>
    {/* Sentences and Words */}
    <div className="flex justify-around w-full mt-2">
      <div className="flex flex-col items-center">
        <span className="text-2xl font-bold text-secondary">
          {formatAccuracy(sentences)}
        </span>
        <span className="text-xs text-content-secondary mt-1">Sentences</span>
      </div>
      <div className="flex flex-col items-center">
        <span className="text-2xl font-bold text-secondary">
          {formatAccuracy(words)}
        </span>
        <span className="text-xs text-content-secondary mt-1">Words</span>
      </div>
    </div>
  </div>
);

// sentencesAccuracy / wordsAccuracy: { correct: number, total: number, ratio: number }
const AccuracyChart = ({ sentencesAccuracy = null, wordsAccuracy = null }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const sTotal = sentencesAccuracy?.total ?? 0;
  const wTotal = wordsAccuracy?.total ?? 0;
  const combinedTotal = sTotal + wTotal;

  const overallAccuracy = combinedTotal > 0
    ? ((sentencesAccuracy?.correct ?? 0) + (wordsAccuracy?.correct ?? 0)) / combinedTotal * 100
    : null;

  const sentencesReviewsAccuracy = sTotal > 0 ? (sentencesAccuracy.correct / sTotal) * 100 : null;
  const wordsReviewAccuracy = wTotal > 0 ? (wordsAccuracy.correct / wTotal) * 100 : null;

  return (
    <div className="w-full flex flex-col h-full">
      {isMobile ? (
        <CompactAccuracyView overall={overallAccuracy} sentences={sentencesReviewsAccuracy} words={wordsReviewAccuracy} />
      ) : (
        <DesktopAccuracyView
          overallAccuracy={overallAccuracy}
          sentencesReviewsAccuracy={sentencesReviewsAccuracy}
          wordsReviewAccuracy={wordsReviewAccuracy}
        />
      )}
    </div>
  );
};

export default AccuracyChart;
